import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';


const PaymentForm = ({ paymentMethod, onSubmit, isProcessing }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  const bankOptions = [
    { value: 'bicec', label: 'BICEC' },
    { value: 'sgbc', label: 'Société Générale Cameroun' },
    { value: 'uba', label: 'UBA Cameroun' },
    { value: 'ecobank', label: 'Ecobank Cameroun' },
    { value: 'afriland', label: 'Afriland First Bank' },
    { value: 'cca', label: 'Crédit Communautaire d\'Afrique' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    switch (paymentMethod?.id) {
      case 'orange_money': case'mtn_momo':
        if (!formData?.phoneNumber) {
          newErrors.phoneNumber = 'Phone number is required';
        } else if (!/^6[0-9]{8}$/?.test(formData?.phoneNumber)) {
          newErrors.phoneNumber = 'Enter valid Cameroon phone number (6XXXXXXXX)';
        }
        break;

      case 'card':
        if (!formData?.cardNumber) {
          newErrors.cardNumber = 'Card number is required';
        } else if (!/^[0-9]{16}$/?.test(formData?.cardNumber?.replace(/\s/g, ''))) {
          newErrors.cardNumber = 'Enter valid 16-digit card number';
        }

        if (!formData?.expiryDate) {
          newErrors.expiryDate = 'Expiry date is required';
        } else if (!/^(0[1-9]|1[0-2])\/[0-9]{2}$/?.test(formData?.expiryDate)) {
          newErrors.expiryDate = 'Enter valid expiry date (MM/YY)';
        }

        if (!formData?.cvv) {
          newErrors.cvv = 'CVV is required';
        } else if (!/^[0-9]{3,4}$/?.test(formData?.cvv)) {
          newErrors.cvv = 'Enter valid CVV';
        }

        if (!formData?.cardholderName) {
          newErrors.cardholderName = 'Cardholder name is required';
        }
        break;

      case 'wallet':
        // No additional validation needed for wallet
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const formatCardNumber = (value) => {
    return value?.replace(/\s/g, '')?.replace(/(.{4})/g, '$1 ')?.trim();
  };

  const formatExpiryDate = (value) => {
    return value?.replace(/\D/g, '')?.replace(/(\d{2})(\d)/, '$1/$2');
  };

  if (!paymentMethod) {
    return (
      <div className="text-center py-8">
        <Icon name="CreditCard" size={48} className="mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Please select a payment method to continue</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name={paymentMethod?.icon} size={20} className="text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Payment Details</h2>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Mobile Money Forms */}
        {(paymentMethod?.id === 'orange_money' || paymentMethod?.id === 'mtn_momo') && (
          <>
            <Input
              label="Phone Number"
              type="tel"
              placeholder="6XXXXXXXX"
              value={formData?.phoneNumber || ''}
              onChange={(e) => handleInputChange('phoneNumber', e?.target?.value)}
              error={errors?.phoneNumber}
              required
              description={`Enter your ${paymentMethod?.name} phone number`}
            />

            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={16} className="text-accent mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-accent mb-1">Payment Instructions</p>
                  <p className="text-muted-foreground">
                    1. You will receive a payment request on your phone\n
                    2. Enter your {paymentMethod?.name} PIN to authorize\n
                    3. Wait for confirmation SMS
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Card Form */}
        {paymentMethod?.id === 'card' && (
          <>
            <Input
              label="Card Number"
              type="text"
              placeholder="1234 5678 9012 3456"
              value={formData?.cardNumber || ''}
              onChange={(e) => handleInputChange('cardNumber', formatCardNumber(e?.target?.value))}
              error={errors?.cardNumber}
              maxLength={19}
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Expiry Date"
                type="text"
                placeholder="MM/YY"
                value={formData?.expiryDate || ''}
                onChange={(e) => handleInputChange('expiryDate', formatExpiryDate(e?.target?.value))}
                error={errors?.expiryDate}
                maxLength={5}
                required
              />
              <Input
                label="CVV"
                type="text"
                placeholder="123"
                value={formData?.cvv || ''}
                onChange={(e) => handleInputChange('cvv', e?.target?.value?.replace(/\D/g, ''))}
                error={errors?.cvv}
                maxLength={4}
                required
              />
            </div>

            <Input
              label="Cardholder Name"
              type="text"
              placeholder="John Doe"
              value={formData?.cardholderName || ''}
              onChange={(e) => handleInputChange('cardholderName', e?.target?.value)}
              error={errors?.cardholderName}
              required
            />

            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Icon name="Shield" size={16} className="text-primary mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-primary mb-1">3D Secure Authentication</p>
                  <p className="text-muted-foreground">
                    Your card will be verified using 3D Secure for additional security.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Wallet Payment */}
        {paymentMethod?.id === 'wallet' && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Wallet" size={32} className="text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Wallet Payment</h3>
            <p className="text-muted-foreground mb-4">
              Payment will be deducted from your AutoCasse wallet balance.
            </p>
            <div className="bg-success/10 border border-success/20 rounded-lg p-3">
              <p className="text-sm text-success">
                ✓ Instant payment • No fees • Secure transaction
              </p>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="default"
          fullWidth
          loading={isProcessing}
          disabled={isProcessing}
          iconName="Lock"
          iconPosition="left"
          iconSize={16}
        >
          {isProcessing ? 'Processing Payment...' : 'Confirm Payment'}
        </Button>

        {/* Security Footer */}
        <div className="flex items-center justify-center space-x-4 pt-4 border-t border-border">
          <div className="flex items-center space-x-1">
            <Icon name="Shield" size={14} className="text-success" />
            <span className="text-xs text-muted-foreground">SSL Encrypted</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Lock" size={14} className="text-success" />
            <span className="text-xs text-muted-foreground">Secure Payment</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="CheckCircle" size={14} className="text-success" />
            <span className="text-xs text-muted-foreground">Verified</span>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PaymentForm;